import { Button } from '@insify/design-system/components';
import { textLgSemibold, textMd, textMdSemibold, textSm } from '@insify/design-system/typography';
import { capitalize } from '@insify/ui-utils';
import clsx from 'clsx';
import { useWatch } from 'react-hook-form';

import { Hint } from '../../../../components/Hint';
import { ReceiptItem } from '../../../../components/ReceiptItem';
import { useTotalPremiumVisibility } from '../../../../components/TotalPremium';
import { useCoveragesForm, useCoveragesPremiums } from '../../useCoveragesForm';
import { SectionHeader } from '../SectionHeader';

import * as styles from './Receipt.module.css';
import { ReceiptContainer } from './ReceiptContainer';

import type { AddOnProps } from '../../../../components/AddOn';
import type { Fee } from '../../../../models/fee';
import type { CoverageParams, CoveragePremiums, ShopContentCommon } from '../../Shop';
import type { MoneyField } from '@insify/global-money';

type Props<CoverageType extends string> = ShopContentCommon & {
  premiums: CoveragePremiums<CoverageType>;
  hasPricingError?: boolean;
  fees?: Fee[];
  addOns?: AddOnProps[];
  receiptItemsAfterAllItems?: React.ReactNode[];
  className?: string;
  hint?: JSX.Element;
};

const DeductibleReceiptItem = <CoverageType extends string, PropertyMultiRiskType extends string>({
  country,
  translations,
}: ShopContentCommon) => {
  const {
    coveragesFormMethods: { control },
  } = useCoveragesForm();
  // @ts-ignore useWatch can't figure out the type because of the generics
  const { deductible = {} } = useWatch<CoverageParams<CoverageType, PropertyMultiRiskType>>({ control });
  const hasDeductible = !!deductible && 'cents' in deductible && 'currency' in deductible;
  return hasDeductible ? (
    <ReceiptItem
      country={country}
      amountClassName={clsx(textMd, styles.deductible)}
      title={translations.receiptDeductible}
      titleClassName={textMd}
      amount={deductible as MoneyField}
      withoutFraction={true}
    />
  ) : (
    <></>
  );
};

const Receipt = <CoverageType extends string>({
  className = '',
  premiums,
  fees,
  hint,
  hasPricingError = false,
  addOns,
  ...content
}: Props<CoverageType>) => {
  const { translations } = content;
  const { ref: refForHidingBottomBar } = useTotalPremiumVisibility();
  const {
    coveragesFormMethods: {
      formState: { isSubmitting },
    },
  } = useCoveragesForm();
  const { isLoading } = useCoveragesPremiums();
  const hasPremiums = !!premiums.coverages?.length;

  return (
    <ReceiptContainer
      className={className}
      header={
        <>
          <SectionHeader>
            <SectionHeader.Content content={content.receiptContent} />
          </SectionHeader>
          {content.receiptDiscountContent}
        </>
      }
      banners={content.translations.receiptUSP && [content.translations.receiptUSP]}
    >
      <div className={clsx(styles.wrapper, className)} ref={refForHidingBottomBar}>
        {hint && <Hint className={styles.hint}>{hint}</Hint>}
        {premiums.coverages?.map(
          ({ type, amount: premiumItem }) =>
            premiumItem && (
              <ReceiptItem
                key={type}
                country={content.country}
                isLoading={isLoading}
                className={clsx(styles.topItem, styles.fullWidth)}
                titleClassName={textMd}
                title={translations[`receipt${capitalize(type)}` as keyof typeof translations] as string}
                amount={premiumItem}
                amountClassName={clsx(textMdSemibold)}
                after={translations.perMonth}
                afterClassName={textSm}
              />
            ),
        )}
        {addOns?.map((addOn, index) => (
          <ReceiptItem
            key={addOn.name}
            country={content.country}
            isLoading={isLoading}
            titleClassName={styles.listItem}
            title={<li>{addOn.title}</li>}
            amountOrPlaceholder={addOn.included && addOn.priceDisplay}
            amount={addOn.priceAmount}
            amountClassName={clsx(textMdSemibold)}
            data-testid={`addon-price-${index}`}
          />
        ))}
        {fees !== undefined && fees.length > 0 && (
          <>
            <hr />
            {fees.map(({ type, amount }) => (
              <ReceiptItem
                key={type}
                country={content.country}
                isLoading={isLoading}
                className={styles.fullWidth}
                titleClassName={textMd}
                title={translations?.fees?.[type]?.title ?? ''}
                description={translations?.fees?.[type]?.description ?? ''}
                amount={amount}
                amountClassName={clsx(textMdSemibold)}
                after={translations.perYear}
                afterClassName={textSm}
              />
            ))}
          </>
        )}
        {hasPremiums && <hr />}
        <ReceiptItem
          country={content.country}
          isLoading={isLoading}
          titleClassName={textLgSemibold}
          title={translations.receiptTotal}
          amount={premiums.total?.amount}
          amountBeforeDiscount={premiums.total?.amountBeforeDiscount}
          amountClassName={clsx(textLgSemibold, styles.priceHighlight)}
          after={translations.perMonth}
          afterClassName={textMd}
          data-testid='receipt-price'
        />
        <hr />
        <DeductibleReceiptItem {...content} />
        {content.receiptItemsAfterAllItems && content.receiptItemsAfterAllItems}
        <Button
          className={styles.submit}
          loading={isLoading}
          disabled={isSubmitting || hasPricingError}
          type='submit'
          kind='primary'
          trackingId='submit'
        >
          {translations.receiptSubmit}
        </Button>
        {content.receiptBelowSubmit}
      </div>
    </ReceiptContainer>
  );
};

export { Receipt };
